import { DashboardTemplate } from 'modules/v2/templates';
import { get } from 'lodash-es';
import { compose } from 'redux';
import { useLocation } from 'react-router-dom';
import { Modal } from 'flowbite-react';
import { useEffect, useState } from 'react';

import { withProfile } from 'modules/v2/containers';
import OrderTag from 'modules/v2/common/AtomicDesign/atoms/OrderTag';
import StepCheckIcon from 'modules/v2/common/components/SvgIcon/StepCheckIcon';
import Button from 'modules/v2/common/AtomicDesign/atoms/Button';
import {
  hasOnlyRMAccess,
  hideBookAccess,
  isDentistCustomer,
  isPilotCustomer,
} from 'modules/v2/utils';
import { getRouteDentistGuidesCatalog } from 'modules/v2/routes/dentist/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import { reAuthenticate } from 'modules/api';
import TTLStorage from 'modules/dashboard/utils/ttl-storage';
import { safeJSONParse } from 'modules/core/jsonUtils';
import { getData } from 'modules/auth';
import { updateIsBooks, updateIsRm } from 'shared/store/actions/bundleAccess';
import { hasBookAccess } from 'modules/dashboard/utils';
import { AnalyticsContainer, BooksContainer, MagazineContainer } from './sections';
import { INTERCOM_CONTROL } from './constants';

const store = new TTLStorage(localStorage);

function checkForReload(userData, data, dispatch) {
  const hasChanges = [
    userData.afy_package !== data.userData.afy_package,
    userData.hasBookAccess !== data.userData.hasBookAccess,
    userData.hasOnlyRMAccess !== data.userData.hasOnlyRMAccess,
  ].some(Boolean);

  if (hasChanges) {
    dispatch(updateIsBooks(hasBookAccess && !hideBookAccess()));
    dispatch(updateIsRm(isPilotCustomer() || hasOnlyRMAccess()));
    return true;
  }

  return false;
}

export const HomePage = ({ history, profile }) => {
  const isDentist = isDentistCustomer();
  const dispatch = useDispatch();
  const { isRm, isBooks } = useSelector((data) => data.bundleAccess);
  const [firstNameUser, setFirstNameUser] = useState('');
  const [welcomeJourneyCompletion, setWelcomeJourneyCompletion] = useState(false);
  const [showOrderId, setShowOrderId] = useState('');
  const location = useLocation();
  const qc = useQueryClient();

  const [refetchCount, setRefetchCount] = useState(0);
  const [customerEmail, setCustomerEmail] = useState('');

  const [userData, setUserData] = useState(() => getData().userData);


  useEffect(() => {
    const original = Storage.prototype.setItem;
    Storage.prototype.setItem = function(key, value) {
      original.call(this, key, value);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue: value,
          storageArea: this,
          url: window.location.href,
        }),
      );
    };
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'userData') {
        const updated = e.newValue ? safeJSONParse(e.newValue) : null;
        setUserData(updated);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [qc]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('userData'));
    if (userData) {
      setCustomerEmail(user?.email);
    }
  }, [customerEmail, setCustomerEmail]);

  useQuery(['reAuthenticate', customerEmail], () => reAuthenticate(customerEmail, true), {
    enabled: Boolean(customerEmail),
    refetchInterval: () => {
      return refetchCount < 10 ? 6000 : false;
    },
    onSuccess: (data) => {
      setRefetchCount(refetchCount + 1);
      const changed = checkForReload(userData, data, dispatch);
      if (changed) {
        setUserData(data.userData);
      }
    },
    onError: (error) => {
      console.error('Authentication failed:', error);
    },
  });

  useEffect(() => {
    if (profile) {
      const { firstName } = get(profile, 'data', '');
      setFirstNameUser(firstName);
    }
  }, [profile]);

  useEffect(() => {
    // TODO: We need to improve that
    if (isDentist) {
      history.push(getRouteDentistGuidesCatalog());
    }

    const intercom = window?.Intercom;
    const intercomShow = localStorage.getItem(INTERCOM_CONTROL);
    if (intercom && !intercomShow) {
      intercom('show');
    }
    localStorage.setItem(INTERCOM_CONTROL, '1');
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const welcomeOrderId = queryParams.get('welcomeOrderId');
    const hasWelcomeJourneyCompleted = queryParams.has('welcomeJourneyCompleted');
    setShowOrderId(welcomeOrderId);
    setWelcomeJourneyCompletion(hasWelcomeJourneyCompleted);
  }, [location.search]);

  return (
    <DashboardTemplate title={`Welcome back ${firstNameUser}`}>
      <div className="rounded-md gap-2">
        <div className="flex gap-x-6 flex-wrap max-xxl:gap-6 justify-between flex-col lg:flex-row">
          <div className="bg-white p-6 rounded-lg shadow-md flex-1 h-[420px] max-lg:h-full">
            <BooksContainer />
          </div>
          {userData?.hasOnlyRMAccess && (
            <div className="bg-white p-6 rounded-lg shadow-md flex-1 h-[420px] max-lg:h-full">
              <MagazineContainer />
            </div>
          )}
        </div>
        <AnalyticsContainer />
      </div>
      <Modal
        show={welcomeJourneyCompletion}
        onClose={() => setWelcomeJourneyCompletion(false)}
        size="xl"
        dismissible
        position="center"
      >
        <Modal.Body className="flex flex-col justify-center items-center gap-4 p-8">
          <div className="w-[50px] h-[50px] bg-success-500 rounded-full p-1.5 flex items-center justify-center">
            <StepCheckIcon stroke="#FFF" width={30} height={30} strokeWidth={3} />
          </div>
          {showOrderId && <OrderTag className="mb-2">Order# {showOrderId}</OrderTag>}
          <h3 className="text-base font-bold">Great Job! 🥳 You have completed all your tasks!</h3>
          <p className="text-sm text-neutral-500 font-normal">
            {showOrderId
              ? 'Your order is confirmed!. Please allow 7-10 days for us to print and ship your order.'
              : 'Fantastic job on taking care of your action steps! '}
          </p>
          <Button
            color="light"
            onClick={() => {
              setWelcomeJourneyCompletion(false);
            }}
          >
            Continue
          </Button>
        </Modal.Body>
      </Modal>
    </DashboardTemplate>
  );
};

export default compose(withProfile)(HomePage);
